
/**
 * 格式化日期时间
 * @param date 日期对象、时间戳或时间字符串
 * @returns 格式化后的日期时间字符串，如：2023-01-01 12:30:45
 */
export const formatDateTime = (date: Date | number | string) => {
    if (typeof date === 'string') {
        date = new Date(Number(date));
    }

    return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(date);
};

/**
 * 格式化时间戳
 * @param timestamp 时间戳字符串或数字
 * @returns 格式化后的日期时间字符串，如：2023-01-01 12:30:45
 */
export const formatTimestamp = (timestamp: string | number) => {
    // 确保时间戳是数字
    const ts = typeof timestamp === 'string' ? Number(timestamp) : timestamp;

    // 检查时间戳是否有效
    if (isNaN(ts) || ts <= 0) {
        return '-';
    }

    // 如果时间戳是毫秒级的（13位），直接使用；如果是秒级的（10位），转换为毫秒
    const date = new Date(ts.toString().length > 10 ? ts : ts * 1000);

    return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(date);
};

/**
 * 人性化时间显示
 * @param date 日期对象、时间戳或时间字符串
 * @returns 人性化的时间描述，如：刚刚、3分钟前、2小时前、昨天、3天前等
 */
export const formatHumanTime = (date: Date | number | string) => {
    let targetDate: Date;
    
    // 统一转换为Date对象
    if (typeof date === 'string') {
        const ts = Number(date);
        targetDate = new Date(ts.toString().length > 10 ? ts : ts * 1000);
    } else if (typeof date === 'number') {
        targetDate = new Date(date.toString().length > 10 ? date : date * 1000);
    } else {
        targetDate = date;
    }

    // 检查日期是否有效
    if (isNaN(targetDate.getTime())) {
        return '-';
    }

    const now = new Date();
    const diffMs = now.getTime() - targetDate.getTime();
    
    // 如果是未来时间，返回具体时间
    if (diffMs < 0) {
        return formatDateTime(targetDate);
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    // 1分钟内：刚刚
    if (diffMinutes < 1) {
        return '刚刚';
    }
    
    // 1小时内：X分钟前
    if (diffHours < 1) {
        return `${diffMinutes}分钟前`;
    }
    
    // 24小时内：X小时前
    if (diffDays < 1) {
        return `${diffHours}小时前`;
    }
    
    // 昨天
    if (diffDays === 1) {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        
        // 检查是否确实是昨天（而不是24小时前）
        if (isSameDay(targetDate, yesterday)) {
            return `昨天 ${targetDate.getHours().toString().padStart(2, '0')}:${targetDate.getMinutes().toString().padStart(2, '0')}`;
        }
    }
    
    // 一周内：X天前
    if (diffDays < 7) {
        return `${diffDays}天前`;
    }
    
    // 今年内：MM-DD HH:mm
    if (targetDate.getFullYear() === now.getFullYear()) {
        return new Intl.DateTimeFormat('zh-CN', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }).format(targetDate);
    }
    
    // 超过一年：完整日期时间
    return formatDateTime(targetDate);
};

/**
 * 判断两个日期是否为同一天
 * @param date1 第一个日期
 * @param date2 第二个日期
 * @returns 是否为同一天
 */
const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
};

/**
 * 获取相对时间描述（简化版）
 * @param date 日期对象、时间戳或时间字符串
 * @returns 简化的相对时间，如：刚刚、5分钟前、2小时前、昨天、3天前
 */
export const formatRelativeTime = (date: Date | number | string) => {
    let targetDate: Date;
    
    // 统一转换为Date对象
    if (typeof date === 'string') {
        const ts = Number(date);
        targetDate = new Date(ts.toString().length > 10 ? ts : ts * 1000);
    } else if (typeof date === 'number') {
        targetDate = new Date(date.toString().length > 10 ? date : date * 1000);
    } else {
        targetDate = date;
    }

    // 检查日期是否有效
    if (isNaN(targetDate.getTime())) {
        return '-';
    }

    const now = new Date();
    const diffMs = now.getTime() - targetDate.getTime();
    
    // 如果是未来时间
    if (diffMs < 0) {
        return '未来';
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return '刚刚';
    if (diffHours < 1) return `${diffMinutes}分钟前`;
    if (diffDays < 1) return `${diffHours}小时前`;
    if (diffDays === 1) return '昨天';
    if (diffDays < 30) return `${diffDays}天前`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}个月前`;
    
    return `${Math.floor(diffDays / 365)}年前`;
};

/**
 * 聊天列表时间显示格式化
 * @param date 日期对象、时间戳或时间字符串
 * @returns 适合聊天列表显示的时间，如：刚刚、15:30、昨天、2天前、12-25
 */
export const formatChatListTime = (date: Date | number | string) => {
    let targetDate: Date;
    
    // 统一转换为Date对象
    if (typeof date === 'string') {
        const ts = Number(date);
        targetDate = new Date(ts.toString().length > 10 ? ts : ts * 1000);
    } else if (typeof date === 'number') {
        targetDate = new Date(date.toString().length > 10 ? date : date * 1000);
    } else {
        targetDate = date;
    }

    // 检查日期是否有效
    if (isNaN(targetDate.getTime())) {
        return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - targetDate.getTime();
    
    // 如果是未来时间，显示具体时间
    if (diffMs < 0) {
        return new Intl.DateTimeFormat('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(targetDate);
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    // 1分钟内：刚刚
    if (diffMinutes < 1) {
        return '刚刚';
    }
    
    // 今天：显示时间 HH:MM
    if (isSameDay(targetDate, now)) {
        return new Intl.DateTimeFormat('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(targetDate);
    }
    
    // 昨天
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (isSameDay(targetDate, yesterday)) {
        return '昨天';
    }
    
    // 一周内：X天前
    if (diffDays < 7) {
        return `${diffDays}天前`;
    }
    
    // 今年内：MM-DD
    if (targetDate.getFullYear() === now.getFullYear()) {
        return new Intl.DateTimeFormat('zh-CN', {
            month: '2-digit',
            day: '2-digit'
        }).format(targetDate);
    }
    
    // 超过一年：YY-MM-DD
    return new Intl.DateTimeFormat('zh-CN', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
    }).format(targetDate);
};